import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { BsFillPatchQuestionFill } from 'react-icons/bs';

import Modal from '../../../../components/modal';
import Input from '../../../../components/input';
import EmptyState from '../../../../components/emptyState';
import { Drawer } from '../../../../components/Drawer';
import { showSuccessMessage, setFormikErrors } from '../../../../hooks/constants';

import close from '../../../../assets/svg-icons/closeBrand.svg';
import arrow from '../../../../assets/svg-icons/blueArrow.svg';
import editIcon from '../../../../assets/svg-icons/edit.svg';
import deleteIcon from '../../../../assets/svg-icons/deleteRed.svg';

export default function EndpointGroupsTab({
  filtered,
  selectedIds,
  toggleAll,
  toggleOne,
  onOpenCreate,
  onRefresh,
  onRequestDelete,
  projectId,
  apiEndPoints,
  setLoading,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [manageModalIndex, setManageModalIndex] = useState(null);

  const rows = useMemo(() => (Array.isArray(filtered) ? filtered : []), [filtered]);

  const rowIds = rows.map((row) => row.id || row.uuid).filter(Boolean);

  const allRowsSelected =
    rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));

  const closeManageModal = () => setManageModalIndex(null);

  const getEndpointType = (row) =>
    row?.endpoint_type || row?.type || row?.endpointType || '—';

  const getEndpointTypeLabel = (row) => {
    const type = getEndpointType(row);
    return type && type !== '—' ? String(type).toUpperCase() : '—';
  };

  const endpointsLabel = (row) => {
    const eps = row?.endpoints;

    if (Array.isArray(eps)) {
      return eps.length ? eps.join(', ') : '—';
    }

    return eps || '—';
  };

  const openEdit = (row) => {
    setEditing(row);
    setEditOpen(true);
    closeManageModal();
  };

  const editForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editing?.name || '',
      description: editing?.description || '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
    }),
    onSubmit: async (values) => {
      if (!projectId || !editing) return;

      setLoading(true);

      try {
        const payload = {
          name: values.name,
        };

        if (values.description?.trim()) {
          payload.description = values.description;
        }

        await apiEndPoints.updateVpnEndpointGroup(
          projectId,
          editing.id || editing.uuid,
          payload,
        );

        showSuccessMessage('Endpoint Group updated');
        setEditOpen(false);
        setEditing(null);
        await onRefresh();
      } catch (error) {
        setFormikErrors(error, editForm.setErrors);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <table className="w-full text-sm text-left overflow-hidden">
        <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] text-sm font-semibold">
          <tr>
            <th className="px-6 py-4 whitespace-nowrap">
              <input
                type="checkbox"
                checked={allRowsSelected}
                onChange={toggleAll}
              />
            </th>
            <th className="px-6 py-4 whitespace-nowrap">Name</th>
            <th className="px-6 py-4 whitespace-nowrap">Description</th>
            <th className="px-6 py-4 whitespace-nowrap">Type</th>
            <th className="px-6 py-4 whitespace-nowrap">Endpoints</th>
            <th className="px-6 py-4 whitespace-nowrap">Actions</th>
          </tr>
        </thead>

        <tbody className="bg-white dark:bg-slate-800 text-black/50 dark:text-white/60 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] text-sm">
          {rows.length === 0 ? (
            <tr>
              <td className="px-6 py-10" colSpan={6}>
                <EmptyState
                  title="No endpoint groups yet"
                  description="Create endpoint groups to model local/peer networks such as subnets or CIDRs."
                  primaryAction={{ label: 'Add Endpoint Group', onClick: onOpenCreate }}
                  secondaryAction={{ label: 'Refresh', onClick: onRefresh }}
                />
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const id = row.id || row.uuid;

              return (
                <tr
                  key={id}
                  className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                >
                  <td className="text-start py-4 px-6">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(id)}
                      onChange={() => toggleOne(id)}
                    />
                  </td>

                  <td className="text-start py-4 px-6">
                    <button
                      type="button"
                      className="text-primaryBlue hover:underline"
                      onClick={() => {
                        setSelected(row);
                        setDetailsOpen(true);
                      }}
                    >
                      {row.name || '—'}
                    </button>
                  </td>

                  <td className="text-start py-4 px-6">
                    {row.description || '—'}
                  </td>

                  <td className="text-start py-4 px-6">
                    {getEndpointTypeLabel(row)}
                  </td>

                  <td className="text-start py-4 px-6">
                    {endpointsLabel(row)}
                  </td>

                  <td className="text-start py-4 px-6 relative">
                    <button
                      className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                      onClick={() => setManageModalIndex(index)}
                      type="button"
                    >
                      Manage Endpoint Group
                    </button>

                    {manageModalIndex === index && (
                      <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
                        <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
                          <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
                            <span className="dark:text-white text-black font-medium">
                              Manage "{row.name}"
                            </span>

                            <img
                              src={close}
                              onClick={closeManageModal}
                              className="h-5 cursor-pointer"
                              alt=""
                            />
                          </div>

                          <div className="p-4 grid gap-1">
                            <div
                              className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all text-black dark:text-white font-medium cursor-pointer"
                              onClick={() => openEdit(row)}
                            >
                              <div className="flex items-center gap-2">
                                <img src={editIcon} alt="" className="h-4" />
                                <span>Edit Endpoint Group</span>
                              </div>
                              <img src={arrow} alt="" />
                            </div>

                            <div
                              className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all text-red-500 font-medium cursor-pointer"
                              onClick={() => {
                                onRequestDelete(id, row.name || id, 'Delete Endpoint Group');
                                closeManageModal();
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <img src={deleteIcon} alt="" className="h-4" />
                                <span>Delete Endpoint Group</span>
                              </div>
                              <img src={arrow} alt="" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <Modal
        title="Edit Endpoint Group"
        isVisible={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditing(null);
        }}
        size="md:w-[55%]"
      >
        <div className="grid md:grid-cols-2 gap-6">
          <form className="grid gap-4" onSubmit={editForm.handleSubmit}>
            <Input
              label="Name *"
              name="name"
              value={editForm.values.name}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              onError={editForm.touched.name ? editForm.errors.name : null}
              isLabelIcon={true}
              iconTitle="The name of the VPN endpoint group."
            />

            <Input
              label="Description"
              name="description"
              value={editForm.values.description}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              onError={editForm.touched.description ? editForm.errors.description : null}
              isLabelIcon={true}
              iconTitle="A description for the VPN endpoint group."
            />

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-xs border-[1.5px] border-[#c4c4c460] rounded-md cursor-pointer bg-white dark:bg-slate-800"
                onClick={() => {
                  setEditOpen(false);
                  setEditing(null);
                }}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="px-4 py-2 text-xs rounded-md cursor-pointer bg-primaryBlue text-white hover:opacity-90"
              >
                Save Changes
              </button>
            </div>
          </form>

          <div className="hidden md:block">
            <p className="text-2xl font-semibold">Description:</p>
            <p className="text-sm opacity-80 mt-1">
              You may update only the endpoint group name and description here.
            </p>
          </div>
        </div>
      </Modal>

      <Drawer
        open={detailsOpen}
        title={selected?.name || 'Endpoint Group'}
        onClose={() => {
          setDetailsOpen(false);
          setSelected(null);
        }}
        width={600}
      >
        {selected ? (
          <div className="grid gap-6">
            <div className="flex items-start justify-between gap-3">
              <div className="text-xl font-semibold">{selected.name}</div>

              <button
                type="button"
                className="text-xs rounded-md px-3 py-2 cursor-pointer bg-white dark:bg-slate-800 border border-[#c4c4c460] hover:bg-brandBlue/10"
                onClick={() => openEdit(selected)}
              >
                Edit Endpoint Group
              </button>
            </div>

            <div>
              <p className="text-lg font-semibold mb-3">Endpoint Group Details</p>

              <div className="border dark:border-[#c4c4c432] rounded-md overflow-hidden">
                {[
                  ['Name', selected.name],
                  ['Description', selected.description || '—'],
                  ['ID', selected.id || selected.uuid],
                  ['Project ID', selected.project_id],
                  ['Type', getEndpointTypeLabel(selected)],
                  ['Endpoints', endpointsLabel(selected)],
                ].map(([key, value]) => {
                  const fieldDescriptions = {
                    Name: 'The name of the VPN endpoint group.',
                    Description: 'A description for the VPN endpoint group.',
                    Type: 'IPsec connection validation requires local endpoints to be subnets and peer endpoints to be CIDRs.',
                    Endpoints:
                      'Remote peer subnet addresses in CIDR format or local subnet IDs, depending on the endpoint group type.',
                  };

                  return (
                    <div
                      key={key}
                      className="grid grid-cols-3 gap-3 px-4 py-3 border-b last:border-b-0 dark:border-[#c4c4c432]"
                    >
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center">
                        {key}
                        {fieldDescriptions[key] && (
                          <BsFillPatchQuestionFill
                            className="text-xs text-primaryBlue ml-1 cursor-pointer"
                            title={fieldDescriptions[key]}
                          />
                        )}
                      </div>

                      <div className="col-span-2 text-sm text-gray-900 dark:text-white">
                        {value ?? '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm opacity-70">No endpoint group selected.</div>
        )}
      </Drawer>
    </>
  );
}
